import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@Utils';

import { Label, LabelSection, LabelToggle, Section, Separator, Switch, ToggleCard } from '@Components';
import { Input } from '@Components/ui-v2/input';
import { ConfigDemand } from '@Models';

interface DemandSectionProps {
  value: ConfigDemand;
  onChange: (value: ConfigDemand) => void;
  className?: string;
  readOnly?: boolean;
}

const DemandSection = ({ value, onChange, className, readOnly = true }: DemandSectionProps) => {
  const { t } = useTranslation();
  const [useVariability, setUseVariability] = useState(value.Variability !== undefined);

  const handleVariabilityToggle = (enabled: boolean) => {
    setUseVariability(enabled);
    if (!enabled) {
      // Remove variability from the config when disabled
      onChange({
        ...value,
        Variability: undefined,
      });
    } else {
      // Set default variability when enabled
      onChange({
        ...value,
        Variability: value.Variability || 0,
      });
    }
  };

  const handleVariabilityChange = (variability: number) => {
    // Ensure variability is within the 0-50% range
    const constrained = Math.max(0, Math.min(50, variability));
    onChange({
      ...value,
      Variability: constrained,
    });
  };

  const indicatorOptions = [
    {
      value: 'SellOut',
      label: t('ConfigurationPolicy.Demand.SellOut'),
      tooltip: t('ConfigurationPolicy.Demand.SellOutTooltip'),
    },
    {
      value: 'SellIn',
      label: t('ConfigurationPolicy.Demand.SellIn'),
      tooltip: t('ConfigurationPolicy.Demand.SellInTooltip'),
    },
    {
      value: 'Forecast',
      label: t('ConfigurationPolicy.Demand.Forecast'),
      tooltip: t('ConfigurationPolicy.Demand.ForecastTooltip'),
    },
  ];

  const demandWindowOptions = [
    { value: 4, label: t('ConfigurationPolicy.Demand.FourWeeks'), tooltip: t('ConfigurationPolicy.Demand.OneMonth') },
    { value: 8, label: t('ConfigurationPolicy.Demand.EightWeeks'), tooltip: t('ConfigurationPolicy.Demand.TwoMonths') },
    {
      value: 12,
      label: t('ConfigurationPolicy.Demand.TwelveWeeks'),
      tooltip: t('ConfigurationPolicy.Demand.ThreeMonths'),
    },
    {
      value: 26,
      label: t('ConfigurationPolicy.Demand.TwentySixWeeks'),
      tooltip: t('ConfigurationPolicy.Demand.SixMonths'),
    },
    {
      value: 52,
      label: t('ConfigurationPolicy.Demand.FiftyTwoWeeks'),
      tooltip: t('ConfigurationPolicy.Demand.OneYear'),
    },
    {
      value: 104,
      label: t('ConfigurationPolicy.Demand.OneHundredFourWeeks'),
      tooltip: t('ConfigurationPolicy.Demand.TwoYears'),
    },
  ];

  return (
    <div className={cn('space-y-6 mt-10', className)}>
      <Section
        left={
          <LabelSection
            label={t('ConfigurationPolicy.Demand.DemandIndicator')}
            description={t('ConfigurationPolicy.Demand.DemandIndicatorDesc')}
          />
        }
        right={
          <div className="space-y-2">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {indicatorOptions.map(({ value: optValue, label, tooltip }) => {
                return (
                  <div key={optValue} className="relative">
                    <ToggleCard selected={value.DemandIndicator === optValue} readOnly={readOnly} tooltip={tooltip}>
                      <div className="flex flex-col">
                        <LabelToggle label={label} />
                      </div>
                    </ToggleCard>
                  </div>
                );
              })}
            </div>
          </div>
        }
      />

      <Separator orientation="horizontal" className="w-full bg-gray-300 h-[1px]" />

      <Section
        left={
          <LabelSection
            label={t('ConfigurationPolicy.Demand.DemandWindow')}
            description={t('ConfigurationPolicy.Demand.DemandWindowDesc')}
          />
        }
        right={
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {demandWindowOptions.map(({ value: optValue, label, tooltip }) => (
                <ToggleCard
                  key={optValue}
                  selected={value.DemandWindow === optValue}
                  readOnly={readOnly}
                  tooltip={tooltip}>
                  <div className="flex flex-col">
                    <LabelToggle label={label} />
                  </div>
                </ToggleCard>
              ))}
            </div>
          </div>
        }
      />

      <Separator orientation="horizontal" className="w-full bg-gray-300 h-[1px]" />

      <Section
        left={
          <LabelSection
            label={t('ConfigurationPolicy.Demand.DemandVariability')}
            description={t('ConfigurationPolicy.Demand.DemandVariabilityDesc')}
          />
        }
        right={
          <div className="flex items-center justify-end">
            <Switch
              id="use-variability"
              checked={useVariability}
              onCheckedChange={handleVariabilityToggle}
              disabled={readOnly}
            />
          </div>
        }
      />

      {useVariability && (
        <>
          <Separator orientation="horizontal" className="w-full bg-gray-300 h-[1px]" />

          <Section
            left={<LabelSection label={t('ConfigurationPolicy.Demand.DemandVariabilityPercent')} description="" />}
            right={
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    readOnly={readOnly}
                    min={0}
                    max={50}
                    value={value.Variability ?? 0}
                    onChange={(e) => handleVariabilityChange(Number(e.target.value))}
                    className="bg-white"
                  />
                  <Label>%</Label>
                </div>
              </div>
            }
          />
        </>
      )}
    </div>
  );
};

export default DemandSection;
